import { StandardEvents } from '@shopify/events';

const STORAGE_KEY = 'awaken.apothecary.v1';
const MAX_ITEMS = 50;
const numericId = value => /^\d{1,20}$/.test(String(value || ''));
const sizeLabel = value => String(value || '').replace(/\b(?:pounds?|lbs?)\.?(?=\s|$)/gi, 'Lb.').replace(/\s+/g, ' ').trim();
const hasNamedSize = value => !!value && String(value) !== 'Default Title';

export const entryKey = item => `${item.type}:${item.id}:${item.variantId || 'product'}`;

export function normalizeSaved(value, origin) {
  if (!Array.isArray(value)) return [];
  const result = [], seen = new Set();
  for (const raw of value.slice(0, 500)) {
    try {
      if (!raw || !['product', 'guide'].includes(raw.type) || !numericId(raw.id)) continue;
      const url = new URL(raw.url, origin);
      const allowed = raw.type === 'product' ? /^\/(?:[a-z]{2}(?:-[a-zA-Z]{2})?\/)?products\/[^/]+$/ : /^\/(?:[a-z]{2}(?:-[a-zA-Z]{2})?\/)?blogs\/[^/]+\/[^/]+$/;
      if (url.origin !== origin || !allowed.test(url.pathname)) continue;
      if (raw.variantId && !numericId(raw.variantId)) continue;
      const item = { type: raw.type, id: String(raw.id), url: url.pathname, title: String(raw.title || '').slice(0, 250), variantId: raw.type === 'product' && raw.variantId ? String(raw.variantId) : null, size: sizeLabel(raw.size).slice(0, 100) };
      const key = entryKey(item);
      if (!seen.has(key) && item.title) { result.push(item); seen.add(key); }
      if (result.length === MAX_ITEMS) break;
    } catch {}
  }
  return result;
}

export class SavedStore {
  constructor(storage, origin) { this.storage = storage; this.origin = origin; this.persistent = true; this.items = []; this.read(); }
  read() {
    try { this.items = normalizeSaved(JSON.parse(this.storage().getItem(STORAGE_KEY) || '[]'), this.origin); }
    catch { this.persistent = false; }
  }
  persist() {
    try { this.storage().setItem(STORAGE_KEY, JSON.stringify(this.items)); this.persistent = true; }
    catch { this.persistent = false; }
  }
  toggle(item) {
    const safe = normalizeSaved([item], this.origin)[0];
    if (!safe) return 'invalid';
    const key = entryKey(safe), index = this.items.findIndex(i => entryKey(i) === key);
    if (index >= 0) { this.items.splice(index, 1); this.persist(); return 'removed'; }
    if (this.items.length >= MAX_ITEMS) return 'limit';
    this.items.push(safe); this.persist(); return 'saved';
  }
  remove(key) { this.items = this.items.filter(item => entryKey(item) !== key); this.persist(); }
  clear() { this.items = []; this.persist(); }
}

export function exactVariant(product, item) {
  if (String(product?.id) !== item.id || !Array.isArray(product.variants)) return null;
  return product.variants.find(v => String(v.id) === item.variantId) || null;
}

function element(tag, className, text) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

export class Apothecary {
  constructor(host) {
    this.host = host; this.dialog = host.querySelector('dialog'); this.component = host.querySelector('dialog-component');
    this.strings = JSON.parse(host.querySelector('[data-apothecary-strings]').textContent);
    this.store = new SavedStore(() => window.localStorage, location.origin);
    this.generation = 0; this.variantStates = new WeakMap(); this.pending = new WeakSet();
    this.dialog.addEventListener('keydown', event => this.keepFocus(event));
    this.sync();
  }
  text(key, values = {}) { let s = this.strings[key] || ''; for (const [k, v] of Object.entries(values)) s = s.replaceAll(`[${k}]`, String(v)); return s; }
  status(text) { this.host.querySelector('[data-apothecary-status]').textContent = text; }
  keepFocus(event) {
    if (event.key !== 'Tab' || !this.dialog.open) return;
    const stops = [...this.dialog.querySelectorAll('button:enabled, a[href], input:not([type="hidden"]):enabled, summary, [tabindex]:not([tabindex="-1"])')]
      .filter(node => {
        const collapsed = node.closest('details:not([open])');
        return !node.closest('[hidden]') && (!collapsed || node === collapsed.querySelector(':scope > summary')) && node.getClientRects().length && getComputedStyle(node).visibility !== 'hidden';
      });
    const first = stops[0], last = stops.at(-1), focused = document.activeElement;
    if (!first) return;
    if (event.shiftKey && (focused === first || focused === this.dialog)) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && (focused === last || focused === this.dialog)) { event.preventDefault(); first.focus(); }
  }
  selection(button) {
    const d = button.dataset;
    const item = { type: d.itemType, id: d.productId, url: d.itemUrl, title: d.itemTitle, variantId: null, size: '' };
    if (d.saveMode === 'size') {
      const scope = button.closest('quick-add-dialog') || button.closest('.shopify-section');
      const form = [...(scope?.querySelectorAll('product-form-component') || [])].find(f => f.dataset.productId === d.productId);
      const picker = [...(scope?.querySelectorAll('variant-picker') || [])].find(p => p.dataset.productId === d.productId);
      const id = form?.querySelector('input[name="id"]')?.value || d.variantId;
      const state = picker && this.variantStates.get(picker);
      if ((picker && this.pending.has(picker)) || !numericId(id)) return null;
      item.variantId = String(id);
      if (item.variantId !== d.variantId && String(state?.id) !== item.variantId) return null;
      item.size = state && String(state.id) === item.variantId ? sizeLabel(state.title || state.name) : d.sizeLabel;
      if (!item.size) return null;
    }
    return item;
  }
  sync() {
    if (!this.host.isConnected) return;
    const count = this.store.items.length;
    for (const button of document.querySelectorAll('[data-apothecary-open]')) {
      button.hidden = false; button.setAttribute('aria-label', this.text('open_count', { count }));
      const badge = button.querySelector('[data-apothecary-count]'); badge.textContent = String(count); badge.hidden = count === 0;
    }
    for (const button of document.querySelectorAll('[data-apothecary-save]')) {
      button.hidden = false;
      const item = this.selection(button); button.disabled = !item;
      const saved = item && this.store.items.some(i => entryKey(i) === entryKey(item));
      button.setAttribute('aria-pressed', String(!!saved));
      let label = this.text(saved ? 'saved' : button.dataset.itemType === 'guide' ? 'save_guide' : 'save_product');
      if (button.dataset.saveMode === 'size' && item && hasNamedSize(item.size)) label = this.text(saved ? 'saved_size' : 'save_size', { size: item.size });
      button.querySelector('[data-apothecary-label]').textContent = label;
      button.setAttribute('aria-label', `${label} — ${button.dataset.itemTitle}`);
    }
    this.host.querySelector('[data-apothecary-storage]').hidden = this.store.persistent;
    this.host.querySelector('[data-apothecary-empty]').hidden = count > 0;
    this.host.querySelector('[data-apothecary-clear-section]').hidden = count === 0;
  }
  save(button) {
    const item = this.selection(button); if (!item) return;
    const result = this.store.toggle(item);
    this.sync(); this.status(this.text(result === 'limit' ? 'limit' : result === 'removed' ? 'removed' : this.store.persistent ? 'saved' : 'temporary'));
    if (this.dialog.open) this.render();
  }
  async open() { await customElements.whenDefined('dialog-component'); this.component.showDialog(); this.render(); }
  row(item) {
    const row = element('article', 'apothecary-item'); row.dataset.itemKey = entryKey(item);
    const top = element('div', 'apothecary-item__top'); top.append(element('h3', '', item.title));
    const remove = element('button', 'apothecary-remove', '×'); remove.type = 'button'; remove.dataset.apothecaryRemove = entryKey(item); remove.setAttribute('aria-label', this.text('remove', { title: item.title })); top.append(remove); row.append(top);
    if (item.variantId && hasNamedSize(item.size)) row.append(element('p', 'apothecary-size', item.size));
    row.append(element('p', 'apothecary-current', this.text('checking')));
    const actions = element('div', 'apothecary-actions');
    const link = element('a', '', this.text(item.type === 'guide' ? 'view_guide' : item.variantId ? 'view_product' : 'choose_size'));
    link.href = item.url + (item.variantId ? `?variant=${item.variantId}` : ''); actions.append(link); row.append(actions);
    return row;
  }
  money(cents) { return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: this.host.dataset.currency }).format(cents / 100); }
  async refresh(item, row, generation) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 8000);
    try {
      const url = item.type === 'product' ? `${item.url}.js` : item.url;
      const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', signal: controller.signal, headers: { Accept: item.type === 'product' ? 'application/json' : 'text/html' } });
      if (generation !== this.generation || !row.isConnected) return;
      const current = row.querySelector('.apothecary-current');
      if (!response.ok) {
        current.textContent = this.text(response.status === 404 ? 'unavailable' : 'check_failed');
        if (response.status === 404) row.querySelector('.apothecary-actions').remove();
        return;
      }
      if (item.type === 'guide') { current.remove(); return; }
      const product = await response.json();
      if (generation !== this.generation || !row.isConnected) return;
      if (String(product.id) !== item.id) { current.textContent = this.text('unavailable'); row.querySelector('.apothecary-actions').remove(); return; }
      row.querySelector('h3').textContent = product.title;
      if (!item.variantId) {
        current.textContent = product.available ? this.text('from', { price: this.money(product.price_min) }) : this.text('sold_out');
        if (product.variants?.length === 1 && !hasNamedSize(product.variants[0].title)) row.querySelector('a').textContent = this.text('view_product');
        return;
      }
      const variant = exactVariant(product, item);
      if (!variant) { current.textContent = this.text('size_changed'); row.querySelector('a').href = item.url; row.querySelector('a').textContent = this.text('choose_size'); return; }
      let size = row.querySelector('.apothecary-size');
      if (hasNamedSize(variant.title)) {
        if (!size) { size = element('p', 'apothecary-size'); current.before(size); }
        size.textContent = sizeLabel(variant.title);
      } else size?.remove();
      current.textContent = `${this.money(variant.price)} · ${this.text(variant.available ? 'in_stock' : 'sold_out')}`;
      if (variant.available && !product.requires_selling_plan) row.append(this.form(item, variant));
    } catch { if (generation === this.generation && row.isConnected) row.querySelector('.apothecary-current').textContent = this.text('check_failed'); }
    finally { clearTimeout(timeout); }
  }
  form(item, variant) {
    const form = element('form', 'apothecary-form'); form.method = 'post'; form.action = this.host.dataset.cartAdd; form.dataset.apothecaryForm = '';
    const id = element('input'); id.type = 'hidden'; id.name = 'id'; id.value = item.variantId; form.append(id);
    const label = element('label', 'apothecary-quantity', this.text('quantity'));
    const quantity = element('input'); quantity.type = 'number'; quantity.name = 'quantity'; quantity.min = String(variant.quantity_rule?.min || 1); quantity.step = String(variant.quantity_rule?.increment || 1); quantity.value = quantity.min;
    if (variant.quantity_rule?.max) quantity.max = String(variant.quantity_rule.max);
    label.append(quantity); form.append(label);
    const add = element('button', 'apothecary-button', this.text(hasNamedSize(variant.title) ? 'add_size' : 'add_product', { size: sizeLabel(variant.title) })); add.type = 'submit'; form.append(add);
    return form;
  }
  async render() {
    const generation = ++this.generation;
    const list = this.host.querySelector('[data-apothecary-list]'); list.replaceChildren();
    const tasks = this.store.items.map(item => { const row = this.row(item); list.append(row); return { item, row }; });
    this.sync();
    let next = 0;
    const work = async () => { while (next < tasks.length && generation === this.generation) { const task = tasks[next++]; await this.refresh(task.item, task.row, generation); } };
    await Promise.all([work(), work(), work()]);
  }
  remove(key) { this.store.remove(key); this.sync(); this.render(); this.status(this.text('removed')); this.host.querySelector('[data-apothecary-close]').focus(); }
  variantEvent(event) {
    if (!(event.target instanceof Element) || event.target.closest('product-card') || !event.promise) return;
    const picker = event.target.closest('variant-picker');
    const id = picker?.dataset.productId; if (!id) return;
    const token = Symbol(); this.pending.add(picker); this.variantStates.set(picker, { token }); this.sync();
    event.promise.then(({ detail }) => {
      if (this.variantStates.get(picker)?.token !== token) return;
      this.pending.delete(picker);
      if (detail?.resource?.id) this.variantStates.set(picker, detail.resource);
      else { this.pending.add(picker); this.variantStates.delete(picker); }
      this.sync();
      queueMicrotask(() => this.sync());
    }).catch(() => { if (this.variantStates.get(picker)?.token === token) this.pending.add(picker); this.sync(); });
  }
}

let active;
function mount() {
  const host = document.querySelector('[data-awaken-apothecary]');
  if (host && active?.host !== host) active = new Apothecary(host);
  if (!host) { active = undefined; for (const button of document.querySelectorAll('[data-apothecary-save],[data-apothecary-open]')) button.hidden = true; }
}
if (typeof document !== 'undefined') {
  mount();
  document.addEventListener('shopify:section:load', mount);
  document.addEventListener('click', event => {
    if (!active?.host.isConnected || !(event.target instanceof Element)) return;
    const target = event.target.closest('[data-apothecary-save],[data-apothecary-open],[data-apothecary-close],[data-apothecary-remove],[data-apothecary-clear]');
    if (!target) return;
    event.preventDefault(); event.stopPropagation();
    if (target.hasAttribute('data-apothecary-save')) active.save(target);
    else if (target.hasAttribute('data-apothecary-open')) active.open();
    else if (target.hasAttribute('data-apothecary-close')) active.component.closeDialog();
    else if (target.hasAttribute('data-apothecary-remove')) active.remove(target.dataset.apothecaryRemove);
    else { active.store.clear(); active.sync(); active.render(); active.status(active.text('removed')); active.host.querySelector('[data-apothecary-close]').focus(); }
  }, true);
  document.addEventListener('submit', event => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.hasAttribute('data-apothecary-form')) return;
    if (form.dataset.submitting === 'true') { event.preventDefault(); return; }
    form.dataset.submitting = 'true'; const add = form.querySelector('button[type="submit"]'); add.disabled = true; add.textContent = active.text('adding');
  }, true);
  document.addEventListener(StandardEvents.productSelect, event => active?.variantEvent(event));
  window.addEventListener('storage', event => { if (event.key === STORAGE_KEY && active) { active.store.read(); active.sync(); if (active.dialog.open) active.render(); } });
  window.addEventListener('pageshow', () => { for (const form of document.querySelectorAll('[data-apothecary-form]')) { delete form.dataset.submitting; form.querySelector('button').disabled = false; } if (active?.dialog.open) active.render(); });
  new MutationObserver(records => {
    if (records.some(r => [...r.addedNodes].some(n => n instanceof Element && (n.matches('[data-apothecary-save]') || n.querySelector('[data-apothecary-save]'))))) active?.sync();
  }).observe(document.body, { childList: true, subtree: true });
}
